import { TAGS } from "../constants";
import {
  CreatePatientSubscriptionArgs,
  PatientSubscriptions,
  PatientSubscriptionsWithPatient,
  SubscriptionReactivation,
  UpdatePatientSubscription,
} from "../types";
import { api } from "./api";

type UpdateRefillDate = UpdatePatientSubscription & {
  clinician: string;
};

export const subscriptionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPatientSubscriptionsById: builder.query<PatientSubscriptions[], number>({
      query: (patientId) => ({
        method: "GET",
        url: `/api/patient-subscriptions/patient/${patientId}`,
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PATIENT_SUBSCRIPTIONS, id: args },
      ],
    }),
    changePlan: builder.mutation<
      PatientSubscriptions,
      {
        priceId: number;
        patient_id: number;
        id: number;
        addPrescription?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/api/patient-subscriptions/${id}/change-plan`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_returns, _error, args) => [
        { type: TAGS.PATIENT_SUBSCRIPTIONS, id: args.patient_id },
      ],
    }),

    cancelSubscription: builder.mutation<
      PatientSubscriptions,
      { id: number; patientId: number }
    >({
      query: ({ id }) => ({
        url: `/api/patient-subscriptions/${id}/cancel`,
        method: "POST",
      }),
      invalidatesTags: (_returns, _error, args) => [
        { type: TAGS.PATIENT_SUBSCRIPTIONS, id: args.patientId },
        {
          type: TAGS.TASKS,
          id: args.patientId,
        },
        {
          type: TAGS.MESSAGE_THREADS,
          id: args.patientId,
        },

        { type: TAGS.ORDERS, id: args.patientId },
        { type: TAGS.INVOICES },
      ],
    }),

    createSubscription: builder.mutation<
      PatientSubscriptions,
      CreatePatientSubscriptionArgs
    >({
      query: (body) => ({
        url: `/api/patient-subscriptions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_returns, _error, args) => [
        { type: TAGS.PATIENT_SUBSCRIPTIONS, id: args.patient_id },
        {
          type: TAGS.MESSAGE_THREADS,
          id: args.patient_id,
        },
      ],
    }),
    getCanceledSubscriptions: builder.query<
      { data: PatientSubscriptionsWithPatient[]; count: number },
      | {
          first_name?: string;
          last_name?: string;
          region?: string;
          patient_id?: string;
          display_name?: string;
          order_by?: string;
          from: number;
          to: number;
        }
      | undefined
    >({
      query: (params) => ({
        method: "GET",
        url: `/api/patient-subscriptions/cancellations`,
        params,
      }),
      providesTags: [TAGS.PATIENT_SUBSCRIPTIONS],
    }),
    updateCurrentPeriodEnd: builder.mutation<
      PatientSubscriptions,
      UpdateRefillDate
    >({
      query: ({ id, ...body }) => ({
        method: "PUT",
        url: `/api/patient-subscriptions/${id}/refill-date`,
        body,
      }),
      invalidatesTags: (_returns, _error, args) => [
        { type: TAGS.PATIENT_SUBSCRIPTIONS, id: args.patient_id },
      ],
    }),
    scheduleReactivation: builder.mutation<
      SubscriptionReactivation,
      { id: number; body: { scheduled_for: string }; patient_id: number }
    >({
      query: ({ id, body }) => ({
        method: "POST",
        url: `/api/patient-subscriptions/${id}/schedule-reactivation`,
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          subscriptionsApi.util.updateQueryData(
            "getScheduledReactivation",
            data.id,
            () => data,
          ),
        );
      },
    }),
    getScheduledReactivation: builder.query<SubscriptionReactivation, number>({
      query: (subscriptionId) => ({
        method: "GET",
        url: `/api/patient-subscriptions/${subscriptionId}/scheduled-reactivation`,
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.SUBSCRIPTION_REACTIVATION, id: args },
      ],
    }),
    reactivateSubscription: builder.mutation<
      PatientSubscriptions | { refill_url: string },
      { id: number; patient_id: number }
    >({
      query: ({ id }) => ({
        method: "POST",
        url: `/api/patient-subscriptions/${id}/reactivate`,
      }),

      invalidatesTags: (_returns, _error, args) => [
        { type: TAGS.PATIENT_SUBSCRIPTIONS, id: args.patient_id },
        {
          type: TAGS.MESSAGE_THREADS,
          id: args.patient_id,
        },
      ],
    }),
    updateScheduledReactivation: builder.mutation<
      SubscriptionReactivation,
      { id: number; body: { scheduled_for: string } }
    >({
      query: ({ id, body }) => ({
        method: "PUT",
        url: `/api/patient-subscriptions/${id}/scheduled-reactivation`,
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        dispatch(
          subscriptionsApi.util.updateQueryData(
            "getScheduledReactivation",
            args.id,
            () => data,
          ),
        );
      },
    }),
  }),
});

export const {
  useGetPatientSubscriptionsByIdQuery,
  useChangePlanMutation,
  useCancelSubscriptionMutation,
  useCreateSubscriptionMutation,
  useGetCanceledSubscriptionsQuery,
  useUpdateCurrentPeriodEndMutation,
  useReactivateSubscriptionMutation,
  useScheduleReactivationMutation,
  useGetScheduledReactivationQuery,
  useUpdateScheduledReactivationMutation,
} = subscriptionsApi;
